import * as Sentry from "@sentry/react"
import { AxiosError } from "axios"

const originalConsoleError = console.error
console.error = (...args) => {
  originalConsoleError.apply(console, args)

  const error = args[0]
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? 0
    if (status >= 400 && status < 500) {
      return
    }
  }

  if (error instanceof Error) {
    Sentry.captureException(error)
  } else {
    const message = args
      .map(arg => (typeof arg === "object" ? JSON.stringify(arg) : String(arg)))
      .join(" ")
    Sentry.captureMessage(message, "error")
  }
}

declare const __APP_VERSION__: string

Sentry.init({
  // Use import.meta.env for Vite environment variables
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Use import.meta.env.MODE for Vite's development/production mode
  environment: import.meta.env.MODE,
  release: __APP_VERSION__,
  integrations: [Sentry.browserTracingIntegration()],
  // Performance Monitoring
  tracesSampleRate: 0.2, // Capture 20% of transactions

  beforeSend(event) {
    const url = event.request?.url || ""
    if (url.startsWith("http://localhost") || url.startsWith("http://127.0.0.1")) {
      return null
    }

    const exception = event.exception?.values?.[0]
    const message = exception?.value || ""

    if (/Request failed with status code 4\d\d/.test(message)) {
      return null
    }

    if (message.includes("removeEventListener") && message.includes("security")) {
      return null
    }

    const hasStacktrace = (exception?.stacktrace?.frames?.length ?? 0) > 0
    if ((message === "" || message === "null") && !hasStacktrace) {
      return null
    }

    return event
  },

  ignoreErrors: [
    /Network Error/,
    /timeout/,
    /Access to fetch at/,
    /^Error: null$/,
    /window\[[^\]]*\]\[[A-Za-z0-9_]+\(\)\]/,
    // Injected by a browser extension / external tracker script (frame is
    // <anonymous>, symbol exists nowhere in our bundle) - not a Speeke bug
    // (SPEEKE-APP-BT).
    /TrackerStorageType is not defined/,
  ],
})
